import { ref, onBeforeUnmount, unref } from '@vue/composition-api';
import { createContext } from '@berlin-front/common/composables/context';
import { camelCase } from '@berlin-front/common/utils';
import { AtoneApiErrorMessagePayload, API_ERROR, broadcast } from '@/broadcast';

/**
 * システムエラー、加盟店エラー、メンテナンスエラーを制御するためのコンテキスト。
 *
 * APIで上記に該当するエラーが発生したとき、エラーメッセージをコンテキストに保持します。
 *
 * 決済失敗とか業務エラーには使いません。
 *
 * - 決済失敗は画面でハンドリング
 * - 業務エラーはcomposables/useErrors
 *
 * を使用して実装します。
 */
export const GlobalApiErrorContext = createContext(() => {
  /**
   * 受診した生のAPIエラーのメッセージを保持します。
   */
  const rawErrorPayload = ref<AtoneApiErrorMessagePayload | undefined>();
  /**
   * APIで発生したエラーをパラメタ別に記録します。
   * param指定のないエラーについては、空文字列をキーとして格納されます。
   */
  const errors = ref<Record<string, string[]>>({});
  /**
   * エラーの発生を記録します。エラーの発生が記録がerrorsに記録済みの場合は、発生したエラーを記録しません。
   * errorsには初めに発生したエラーのみを保持し、以降、クリアが実行されるまでは新しいエラーをうけつけません。
   * これは、並列でAPIをコールしている場合、エラー画面への遷移や画面へのエラーメッセージの書き込みが重複して発生することを
   * 回避するために行っています。
   */
  const hasErrors = ref(false);
  /**
   * 全てのエラーメッセージをクリアします。
   * モーダルを閉じて、エラー画面を初期画面に戻す際にこれを使用して初期化する必要があります。
   */
  const clearAll = () => {
    errors.value = {};
    rawErrorPayload.value = undefined;
    hasErrors.value = false;
  };

  // TODO: リトライを諦めるケースはここでハンドリングしない。実現できてるかどうか。
  /**
   * messageとして配信される、Axiosで発生したAPIエラーをキャプチャするためのリスナーです。
   *
   * エラーキャプチャー後は、各エラー画面へと遷移します。
   *
   */
  const unlisten = broadcast.listen(API_ERROR, (payload) => {
    if (!unref(hasErrors) && payload.errorType !== 'BUSINESS_ERROR') {
      hasErrors.value = true;
      rawErrorPayload.value = payload;
      if (!payload.errorResponse?.details) {
        // TODO: detailsからエラー情報が取得できない場合、メッセージどうするか決める。
      } else if (typeof payload.errorResponse.details !== 'string') {
        // detailsが通常のエラー型の場合。
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        payload.errorResponse.details.errors.forEach((error) => {
          const params = error.params.length > 0 ? error.params : [''];
          const value = { ...errors.value };
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          params
            .map((paramName) => camelCase(paramName))
            .forEach((paramName) => {
              value[paramName] = [...(value[paramName] ?? []), ...error.messages];
            });
          errors.value = value;
        });
      } else {
        // detailsが文字列の場合
        errors.value = { '': [payload.errorResponse.details] };
      }
    }
  });
  onBeforeUnmount(() => {
    unlisten();
  });

  return {
    rawErrorPayload,
    errors,
    clearAll,
  };
});
